import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useDashboardStats,
  type DashboardStats,
} from "@/features/dashboard/hooks";
import {
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  ArrowUpTrayIcon,
  UserPlusIcon,
  ChartBarIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

type StatTone = "accent" | "warn" | "ok";

const TONE_ICON: Record<StatTone, string> = {
  accent: "bg-accent-tint text-accent",
  warn: "bg-warn-bg text-warn",
  ok: "bg-ok-bg text-ok",
};

function StatCard({
  icon: Icon,
  label,
  value,
  sublabel,
  tone = "accent",
  valueTone,
  sublabelTone,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  sublabel: string;
  tone?: StatTone;
  valueTone?: "warn";
  sublabelTone?: "ok";
}) {
  return (
    <div className="rounded-2xl border border-subtle bg-surface p-[17px]">
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-text-faint">
          {label}
        </span>
        <span
          className={`flex h-[30px] w-[30px] items-center justify-center rounded-[9px] ${TONE_ICON[tone]}`}
        >
          <Icon className="h-[15px] w-[15px]" />
        </span>
      </div>
      <div
        className={`mt-3.5 font-serif text-[42px] leading-none ${
          valueTone === "warn" ? "text-warn" : "text-text-base"
        }`}
      >
        {value}
      </div>
      <div
        className={`mt-1.5 text-xs ${
          sublabelTone === "ok" ? "text-ok" : "text-text-faint"
        }`}
      >
        {sublabel}
      </div>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div className="animate-pulse rounded-2xl border border-subtle bg-surface p-[17px]">
      <div className="h-3 w-24 rounded bg-surface-2" />
      <div className="mt-4 h-10 w-16 rounded bg-surface-2" />
      <div className="mt-2 h-3 w-20 rounded bg-surface-2" />
    </div>
  );
}

function statusPill(status: string) {
  const s = (status || "").toUpperCase();
  if (s === "INDEXED")
    return { cls: "bg-ok-bg text-ok", dot: "bg-ok-dot", label: "INDEXED" };
  if (s === "FAILED")
    return { cls: "bg-err-bg text-err", dot: "bg-err-dot", label: "FAILED" };
  if (["PROCESSING", "QUEUED", "PROCESSED"].includes(s))
    return { cls: "bg-warn-bg text-warn", dot: "bg-warn-dot", label: s };
  return {
    cls: "bg-surface-2 text-text-muted",
    dot: "bg-text-faint",
    label: s,
  };
}

function RecentDocuments({
  documents,
}: {
  documents: DashboardStats["recentDocuments"];
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-subtle bg-surface">
      <div className="flex items-center justify-between border-b border-subtle px-[18px] py-[15px]">
        <span className="text-sm font-semibold text-text-base">
          Recent documents
        </span>
        <Link
          to="/documents"
          className="text-[12.5px] font-medium text-accent hover:underline"
        >
          View all →
        </Link>
      </div>
      {documents.length === 0 ? (
        <p className="px-[18px] py-6 text-sm text-text-faint">
          No documents uploaded yet.
        </p>
      ) : (
        documents.map((doc) => {
          const pill = statusPill(doc.status);
          return (
            <Link
              key={doc.id}
              to={`/documents/${doc.id}`}
              className="flex items-center gap-3 border-b border-subtle px-[18px] py-[11px] last:border-b-0 hover:bg-surface-2"
            >
              <span className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg bg-file-bg">
                <DocumentTextIcon className="h-[15px] w-[15px] text-file" />
              </span>
              <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-text-base">
                {doc.filename}
              </span>
              <span
                className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-[3px] text-[11px] font-semibold ${pill.cls}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${pill.dot}`} />
                {pill.label}
              </span>
              <span className="w-16 flex-shrink-0 text-right font-mono text-xs text-text-faint">
                {new Date(doc.createdAt).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </Link>
          );
        })
      )}
    </div>
  );
}

function QuickAction({
  icon: Icon,
  label,
  to,
}: {
  icon: React.ElementType;
  label: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 border-b border-subtle px-[18px] py-[13px] last:border-b-0 hover:bg-surface-2"
    >
      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[9px] bg-accent-tint text-accent">
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex-1 text-[13.5px] font-medium text-text-base">
        {label}
      </span>
      <ChevronRightIcon className="h-4 w-4 text-text-dim" />
    </Link>
  );
}

const HomePage = () => {
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const { stats, isLoading, isError } = useDashboardStats();

  const answeredPct =
    stats && stats.queries.last30Days > 0
      ? Math.round(
          (stats.unansweredLast30Days / stats.queries.last30Days) * 100,
        )
      : 0;

  return (
    <div className="px-7 py-6">
      {/* Hero */}
      <div className="mb-[22px] flex items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-[32px] font-medium tracking-tight text-text-base">
            Welcome back, {user?.firstName || "there"}
          </h1>
          <div className="mt-1 font-mono text-[12.5px] text-text-faint">
            System overview
          </div>
        </div>
        <Link
          to="/chat"
          className="inline-flex h-[38px] flex-shrink-0 items-center gap-2 rounded-[9px] bg-accent px-4 text-[13.5px] font-semibold text-white transition-colors hover:bg-accent-strong"
        >
          <ChatBubbleLeftRightIcon className="h-4 w-4" />
          Ask the docs
        </Link>
      </div>

      {isError && (
        <div className="mb-6 rounded-lg border border-err bg-err-bg p-4 text-sm text-err">
          Failed to load dashboard statistics. Please try refreshing the page.
        </div>
      )}

      {/* Stat cards */}
      <div className="mb-4 grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        {isLoading || !stats ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              icon={DocumentTextIcon}
              label="Indexed Documents"
              value={stats.documents.indexed}
              sublabel={`of ${stats.documents.total} total`}
            />
            <StatCard
              icon={ChatBubbleLeftRightIcon}
              label="Queries This Week"
              value={stats.queries.last7Days}
              sublabel={`${stats.queries.last30Days} in the last 30 days`}
            />
            <StatCard
              icon={QuestionMarkCircleIcon}
              label="Unanswered · 30d"
              value={stats.unansweredLast30Days}
              tone="warn"
              valueTone="warn"
              sublabel={`${answeredPct}% of responses`}
            />
            <StatCard
              icon={CheckCircleIcon}
              label="Processing Issues"
              value={stats.stuckDocuments}
              tone="ok"
              sublabel={
                stats.stuckDocuments === 0
                  ? "all systems healthy"
                  : "need attention"
              }
              sublabelTone={stats.stuckDocuments === 0 ? "ok" : undefined}
            />
          </>
        )}
      </div>

      {/* Recent documents + quick actions */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.7fr_1fr]">
        {isLoading || !stats ? (
          <div className="h-64 animate-pulse rounded-2xl border border-subtle bg-surface" />
        ) : (
          <RecentDocuments documents={stats.recentDocuments} />
        )}

        <div className="overflow-hidden rounded-2xl border border-subtle bg-surface">
          <div className="border-b border-subtle px-[18px] py-[15px] text-sm font-semibold text-text-base">
            Quick actions
          </div>
          <QuickAction
            icon={ArrowUpTrayIcon}
            label="Upload a document"
            to="/documents/upload"
          />
          <QuickAction
            icon={ChatBubbleLeftRightIcon}
            label="Ask a question"
            to="/chat"
          />
          {isAdmin && (
            <QuickAction
              icon={UserPlusIcon}
              label="Invite a user"
              to="/users/create"
            />
          )}
          <QuickAction
            icon={ChartBarIcon}
            label="Review unanswered queries"
            to="/analytics"
          />
        </div>
      </div>
    </div>
  );
};

export default HomePage;
